import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { toast } from 'react-hot-toast';
import pendingActionService from '../services/pendingActionService';
import './DevLogin.css';

const DevLogin = () => {
  const [selectedPersona, setSelectedPersona] = useState('USER');
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('dev');
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const navigate = useNavigate();

  const personas = ['USER', 'ADMIN', 'AFFILIATE', 'BUSINESS_OWNER', 'BUSINESS_EMPLOYEE', 'UNAUTHENTICATED'];

  useEffect(() => {
    if (selectedPersona === 'AFFILIATE') {
      fetchAffiliates();
    }
  }, [selectedPersona]);

  const fetchAffiliates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dev/affiliates`);
      if (!response.ok) {
        throw new Error('Failed to fetch affiliates');
      }
      const data = await response.json();
      setAffiliates(data.affiliates || []);
    } catch (err) {
      console.error('Error fetching affiliates:', err);
      setError(err.message);
    }
  };

  // Fetch user status when USER persona is selected
  const fetchUserStatus = async () => {
    setStatusLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dev/user-status/dev_user`);
      if (response.ok) {
        const data = await response.json();
        setUserStatus(data);
      }
    } catch (err) {
      console.error('Error fetching user status:', err);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPersona === 'USER') {
      fetchUserStatus();
    }
  }, [selectedPersona]);

  // Create test menus for regular user
  const handleCreateTestMenus = async () => {
    setStatusLoading(true);
    setActionMessage(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dev/create-test-menus/dev_user`, {
        method: 'POST'
      });
      const data = await response.json();
      setActionMessage({ type: 'success', text: data.message });
      fetchUserStatus();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setStatusLoading(false);
    }
  };

  // Reset user to non-affiliate
  const handleResetAffiliateStatus = async () => {
    setStatusLoading(true);
    setActionMessage(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dev/reset-affiliate-status/dev_user`, {
        method: 'POST'
      });
      const data = await response.json();
      setActionMessage({ type: 'success', text: data.message });
      fetchUserStatus();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setStatusLoading(false);
    }
  };

  // Delete test menus
  const handleDeleteTestMenus = async () => {
    setStatusLoading(true);
    setActionMessage(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dev/test-menus/dev_user`, {
        method: 'DELETE'
      });
      const data = await response.json();
      setActionMessage({ type: 'success', text: data.message });
      fetchUserStatus();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Handle unauthenticated mode - clear storage and redirect
      if (selectedPersona === 'UNAUTHENTICATED') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        console.log('Cleared authentication, testing as unauthenticated user');
        window.location.href = '/meal-plan'; // Redirect to preview page
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dev/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          persona: selectedPersona,
          affiliateId: selectedPersona === 'AFFILIATE' ? selectedAffiliateId : undefined
        })
      });

      const data = await response.json();
      console.log('Login response:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store user data and token
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      console.log('Stored user data:', data.user);
      console.log('User roles - Admin:', data.user.isAdmin, 'Affiliate:', data.user.isAffiliate);

      // Check for pending action after login (e.g., accept coach invite)
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      
      if (redirectUrl) {
        console.log('[DevLogin] Pending action executed, redirecting to:', redirectUrl);
        toast.success('Successfully connected with your coach!');
        // Use window.location.href to force full app reload with new auth state
        window.location.href = redirectUrl;
        return;
      }

      // Force a page reload to update the app state
      window.location.href = data.user.isAdmin ? '/admin/dashboard' 
                          : data.user.isBusinessOwner ? '/business/dashboard'
                          : data.user.isBusinessEmployee ? '/business/employee/dashboard'
                          : data.user.isAffiliate 
                            ? (data.user.specialization === 'fitness' ? '/coach/dashboard' : '/affiliate/dashboard')
                          : '/menu';
      
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError(null);
    try {
      if (!credentialResponse.credential) {
        throw new Error('Missing authentication credentials');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data = await response.json();
      const userData = { ...data.user, sub: data.user.sub || data.user.id || data.user.email };

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);

      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      if (redirectUrl) {
        toast.success('Logged in! Redirecting...');
        window.location.href = redirectUrl;
      } else {
        toast.success(`Logged in as ${userData.name || userData.email}`);
        window.location.href = userData.isAdmin ? '/admin/dashboard'
          : userData.isBusinessOwner ? '/business/dashboard'
          : userData.isBusinessEmployee ? '/business/employee/dashboard'
          : userData.isAffiliate ? '/coach/dashboard'
          : '/menu';
      }
    } catch (err) {
      console.error('Google auth error:', err);
      setError(err.message || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dev-login" onClick={(e) => {
      if (e.target.className === 'dev-login') {
        navigate('/menu');
      }
    }}>
      <div className="dev-login-card" onClick={(e) => e.stopPropagation()}>
        <button 
          className="close-modal-btn"
          onClick={() => navigate('/menu')}
          aria-label="Close"
        >
          ×
        </button>
        <h2>Development Login</h2>
        <p className="dev-notice">This login is only available in development mode</p>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="dev-login-form-group">
            <label htmlFor="persona">Select Persona:</label>
            <select
              id="persona"
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
              disabled={loading}
            >
              {personas.map(persona => {
                const labels = {
                  USER: 'User User',
                  ADMIN: 'Admin User',
                  AFFILIATE: 'Affiliate User',
                  BUSINESS_OWNER: 'Business Owner',
                  BUSINESS_EMPLOYEE: 'Business Employee',
                  UNAUTHENTICATED: 'Unauthenticated User'
                };
                return (
                  <option key={persona} value={persona}>
                    {labels[persona] || persona}
                  </option>
                );
              })}
            </select>
          </div>

          {selectedPersona === 'AFFILIATE' && (
            <div className="dev-login-form-group">
              <label htmlFor="affiliateId">Select Affiliate:</label>
              <select
                id="affiliateId"
                value={selectedAffiliateId}
                onChange={(e) => setSelectedAffiliateId(e.target.value)}
                disabled={loading}
              >
                <option value="dev">Dev Affiliate</option>
                {affiliates.map(affiliate => (
                  <option key={affiliate.id} value={affiliate.id}>
                    {affiliate.businessName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="persona-info">
            <h3>Selected Persona Details:</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Role:</span>
                <span className="value">{selectedPersona}</span>
              </div>
              {selectedPersona === 'UNAUTHENTICATED' ? (
                <>
                  <div className="info-item">
                    <span className="label">Access Level:</span>
                    <span className="value">Preview Mode</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Features:</span>
                    <span className="value">View Only</span>
                  </div>
                </>
              ) : selectedPersona === 'BUSINESS_OWNER' ? (
                <>
                  <div className="info-item">
                    <span className="label">Role:</span>
                    <span className="value">Business Owner</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Organization:</span>
                    <span className="value">Dev Physical Therapy</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Dashboard:</span>
                    <span className="value">Business Admin Dashboard</span>
                  </div>
                </>
              ) : selectedPersona === 'BUSINESS_EMPLOYEE' ? (
                <>
                  <div className="info-item">
                    <span className="label">Role:</span>
                    <span className="value">Business Employee</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Organization:</span>
                    <span className="value">Dev Physical Therapy</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Note:</span>
                    <span className="value" style={{ fontSize: '0.85em', color: '#666' }}>
                      Login as Business Owner first to create org
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Dashboard:</span>
                    <span className="value">Employee Dashboard</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="info-item">
                    <span className="label">Admin Access:</span>
                    <span className="value">{selectedPersona === 'ADMIN' ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Affiliate Status:</span>
                    <span className="value">{selectedPersona === 'AFFILIATE' ? 'Active' : 'No'}</span>
                  </div>
                  {selectedPersona === 'AFFILIATE' && selectedAffiliateId !== 'dev' && (
                    <div className="info-item">
                      <span className="label">Business:</span>
                      <span className="value">
                        {affiliates.find(a => a.id === selectedAffiliateId)?.businessName || 'Loading...'}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <button 
            type="submit" 
            className="dev-login-button"
            disabled={loading}
          >
            {loading ? (selectedPersona === 'UNAUTHENTICATED' ? 'Clearing session...' : 'Logging in...') 
                     : (selectedPersona === 'UNAUTHENTICATED' ? 'Test as Unauthenticated User' : 'Login as Selected Persona')}
          </button>
        </form>

        {/* Google Login Section */}
        <div className="dev-google-section">
          <div className="dev-divider">
            <span>or sign in with a real account</span>
          </div>
          <div className="dev-google-login-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Google Login Failed')}
              text="signin_with"
              shape="rectangular"
              size="large"
              width="100%"
            />
          </div>
          <p className="dev-google-hint">
            Use your real Google account to test with actual user data, subscriptions, and integrations.
          </p>
        </div>

        {/* Testing Panel for USER persona */}
        {selectedPersona === 'USER' && (
          <div className="dev-testing-panel">
            <h3>🧪 Testing Tools</h3>
            <p className="testing-info">
              Use these tools to test the user-to-affiliate transition flow
            </p>

            {actionMessage && (
              <div className={`action-message ${actionMessage.type}`}>
                {actionMessage.text}
              </div>
            )}

            {statusLoading ? (
              <div className="status-loading">Loading...</div>
            ) : userStatus ? (
              <div className="user-status">
                <div className="status-row">
                  <span className="status-label">Affiliate Status:</span>
                  <span className={`status-value ${userStatus.user.isAffiliate ? 'active' : 'inactive'}`}>
                    {userStatus.user.isAffiliate ? '✅ Is Affiliate' : '❌ Not Affiliate'}
                  </span>
                </div>
                <div className="status-row">
                  <span className="status-label">Personal Menus:</span>
                  <span className="status-value">{userStatus.menuCounts.personalMenus}</span>
                </div>
                {userStatus.affiliate && (
                  <>
                    <div className="status-row">
                      <span className="status-label">Affiliate Menus:</span>
                      <span className="status-value">{userStatus.menuCounts.affiliateMenus}</span>
                    </div>
                    <div className="status-row">
                      <span className="status-label">Business:</span>
                      <span className="status-value">{userStatus.affiliate.businessName}</span>
                    </div>
                  </>
                )}
              </div>
            ) : null}

            <div className="testing-actions">
              <button
                className="test-action-btn create-menus"
                onClick={handleCreateTestMenus}
                disabled={statusLoading}
                title="Create 3 test personal menus for the regular user"
              >
                <i className="fas fa-plus"></i> Create Test Menus
              </button>
              
              {/* Show reset button if user is affiliate OR has an affiliate record */}
              {(userStatus?.user?.isAffiliate || userStatus?.affiliate) && (
                <button
                  className="test-action-btn reset-affiliate"
                  onClick={handleResetAffiliateStatus}
                  disabled={statusLoading}
                  title="Reset user to non-affiliate status to test the conversion flow again"
                >
                  <i className="fas fa-undo"></i> Reset to Non-Affiliate
                </button>
              )}
              
              <button
                className="test-action-btn delete-menus"
                onClick={handleDeleteTestMenus}
                disabled={statusLoading}
                title="Delete all test menus (keeps manually created ones)"
              >
                <i className="fas fa-trash"></i> Delete Test Menus
              </button>
            </div>

            <div className="testing-instructions">
              <h4>Testing Flow:</h4>
              <ol>
                <li>Click "Create Test Menus" to add personal menus</li>
                <li>Login as "Regular User" and go to "My Menus"</li>
                <li>Click "Become a Creator / Coach" in the footer</li>
                <li>Complete registration → Dashboard should show migration notice</li>
                <li>Use "Reset to Non-Affiliate" to test again</li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevLogin; 